import { PropertyRow } from './Layer';
import { ChoroplethLegend } from './ChoroplethLegend';
import { EriStepSelect } from './EriStepSelect';
import {
  useEriLayerControls,
  usePowerOutageLayerControls,
} from './useOutageLayerControls';
import {
  ERI_CLASS_BREAKS,
  POWER_OUTAGE_CLASS_BREAKS,
} from './utils/outageLayerStyles';

const formatCount = (n: number) => n.toLocaleString('en-US');

function StatusNote({
  isVisible,
  isLoading,
  isError,
  fetched,
  hasData,
  emptyText,
}: {
  isVisible: boolean;
  isLoading: boolean;
  isError: boolean;
  fetched: boolean;
  hasData: boolean;
  emptyText: string;
}) {
  if (!isVisible && !fetched) {
    return (
      <p className="small text-muted mb-0">
        Turn the layer on to load the latest data.
      </p>
    );
  }
  if (isLoading) return <p className="small text-muted mb-0">Loading…</p>;
  if (isError)
    return <p className="small text-danger mb-0">Failed to load layer data.</p>;
  if (fetched && !hasData)
    return <p className="small text-muted mb-0">{emptyText}</p>;
  return null;
}

/**
 * Expanded content for the "Power Outages" row (shared by the Layers tab and
 * SitStat).
 */
export function PowerOutageLayerBody({
  controls,
}: {
  controls: ReturnType<typeof usePowerOutageLayerControls>;
}) {
  return (
    <>
      <StatusNote
        isVisible={controls.isVisible}
        isLoading={controls.isLoading}
        isError={controls.isError}
        fetched={controls.fetched}
        hasData={controls.hasData}
        emptyText="No data — the PowerOutage.us feed has not delivered a county snapshot."
      />
      <PropertyRow label="As of" value={controls.asOfLabel} />
      <PropertyRow
        label="Counties"
        value={controls.hasData ? formatCount(controls.featureCount) : null}
      />
      <PropertyRow
        label="Customers out"
        value={
          controls.summary && controls.hasData
            ? `${formatCount(controls.summary.out)} of ${formatCount(controls.summary.tracked)} tracked`
            : null
        }
      />
      <PropertyRow
        label="Source"
        value="PowerOutage.us (hourly, county level)"
      />
      <ChoroplethLegend
        breaks={POWER_OUTAGE_CLASS_BREAKS}
        title="Percent of customers out"
      />
    </>
  );
}

/**
 * Expanded content for the "Outage Prediction (ERI)" row (shared by the Layers
 * tab and SitStat).
 */
export function EriLayerBody({
  controls,
}: {
  controls: ReturnType<typeof useEriLayerControls>;
}) {
  return (
    <>
      <StatusNote
        isVisible={controls.isVisible}
        isLoading={controls.isLoading}
        isError={controls.isError}
        fetched={controls.fetched}
        hasData={controls.hasData}
        emptyText="No data — no ERI model run is available."
      />
      <PropertyRow label="Model run" value={controls.modelRuntimeLabel} />
      {controls.staleLabel ? (
        <p className="small text-warning mb-0">{controls.staleLabel}</p>
      ) : null}
      <PropertyRow label="Showing" value={controls.selectedStepLabel} />
      <EriStepSelect
        options={controls.stepOptions}
        value={controls.selectedStep}
        onChange={controls.setStep}
        disabled={!controls.isVisible}
      />
      <PropertyRow
        label="Counties"
        value={controls.hasData ? formatCount(controls.featureCount) : null}
      />
      <ChoroplethLegend
        breaks={ERI_CLASS_BREAKS}
        title="ERI storm level (mean scenario)"
      />
    </>
  );
}
